import type { HttpClient } from '../http/HttpClient'
import { parseDueState } from '@/domain/sla/dueState'
import { PENDING_KINDS, type InboxPage, type InboxRepository, type PendingGroup, type PendingKind, type PendingSummary } from '@/domain/inbox/Inbox'

const UNPREFIXED: ReadonlySet<PendingKind> = new Set(['INVOICE', 'PROVIDER_APPROVAL'])

interface RawPendingItem {
  id: number
  number: string | null
  detail: string | null
  amount: number | null
  currency: string | null
  days: number | null
  due_state?: string | null
  society?: string | null
  request_group?: string | null
  request_group_number?: number | null
  action?: string | null
  issue_code: string | null
}

interface RawPendingTab {
  tab: string
  count: number
  oldest_days: number | null
  due_state?: string | null
}

interface RawPendingSummary {
  total: number
  groups: { kind: PendingKind, count: number, oldest_days: number | null, due_state?: string | null, items: RawPendingItem[], tabs?: RawPendingTab[] }[]
}

interface RawNotification {
  id: number
  triggering_entity: string
  event_type: string
  title: string
  template_data: Record<string, unknown> | null
  read_at: string | null
  created_at: string
}

interface RawNotificationPage {
  count: number
  results: RawNotification[]
}

export class HttpInboxRepository implements InboxRepository {
  constructor(private readonly http: HttpClient) {}

  async pending(limit: number): Promise<PendingSummary> {
    const raw = await this.http.url('purchases/pending').params({ limit: String(limit) }).get<RawPendingSummary>()
    return {
      total: raw.total,
      groups: raw.groups.filter((group) => PENDING_KINDS.includes(group.kind)).flatMap((group): PendingGroup[] => {
        const items = group.items.map((item) => ({
          id: item.id,
          kind: group.kind,
          number: item.number === null || UNPREFIXED.has(group.kind) ? item.number : `#${item.number}`,
          detail: item.detail,
          amount: item.amount,
          currency: item.currency,
          days: item.days,
          dueState: parseDueState(item.due_state),
          society: item.society ?? null,
          requestGroup: item.request_group ?? null,
          requestGroupNumber: item.request_group_number ?? null,
          action: item.action ?? null,
          issueCode: item.issue_code ?? null,
        }))
        const tabs = group.tabs ?? []
        if (tabs.length === 0) {
          return [{ kind: group.kind, tab: null, showsTab: false, count: group.count, oldestDays: group.oldest_days, dueState: parseDueState(group.due_state), items }]
        }
        return tabs.map((tab, index) => ({
          kind: group.kind,
          tab: tab.tab,
          showsTab: tabs.length > 1,
          count: tab.count,
          oldestDays: tab.oldest_days,
          dueState: parseDueState(tab.due_state),
          items: index === 0 ? items : [],
        }))
      }),
    }
  }

  async notifications(pageSize: number, unread = false): Promise<InboxPage> {
    const params: Record<string, string> = unread ? { unread: 'true', page_size: String(pageSize) } : { page_size: String(pageSize) }
    const raw = await this.http.url('notifications').params(params).get<RawNotificationPage>()
    return {
      count: raw.count,
      notifications: raw.results.map((row) => ({
        id: row.id,
        entity: row.triggering_entity,
        event: row.event_type,
        title: row.title,
        data: row.template_data ?? {},
        readAt: row.read_at,
        createdAt: row.created_at,
      })),
    }
  }

  async markRead(ids: number[]): Promise<void> {
    if (ids.length === 0) return
    await this.http.url('notifications/read').body({ ids }).post()
  }
}
